// Service to manage questions with localStorage/Supabase integration for TEF Canada Trainer
import { READING_STRUCTURE, normalizeReadingSection, getReadingLabelByKey, LISTENING_STRUCTURE, normalizeListeningSection, getListeningLabelByKey, WRITING_STRUCTURE, normalizeWritingSection, getWritingLabelByKey } from "@/data/tefStructure";
import { buildFullReadingTestQuestions, buildSectionExamQuestions, buildDiagnosticQuestions, isWritingSectionA, isWritingSectionB } from "@/utils/questionSectionUtils";
import { mediaService } from "./mediaService";
import { type UploadedMedia } from "./storageService";
import { streakService } from "./streakService";
import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";
import { adminService } from "./adminService";
import { validateQuestion, normalizeQuestionSubSection, extractQuestionFromBody } from "@/utils/questionValidation";
import { normalizeQuestionCompetency, buildQuestionImportFingerprint } from "@/utils/questionNormalizer";
import { buildCanonicalQuestionFingerprint as buildQuestionFingerprint, buildQuestionFingerprintHash } from "@/utils/questionFingerprint";
import { selectRandomItems } from "@/utils/testRandomizer";

export interface QuestionOption {
  label: "A" | "B" | "C" | "D";
  text?: string;
  imageUrl?: string;
  mediaId?: string; // Point to IndexedDB blob
}

export interface Question {
  id: string;
  title: string;
  competence: "Reading" | "Listening" | "Writing";
  section: string;
  sectionKey?: string; // Optional official structured key
  difficulty: "Fácil" | "Médio" | "Difícil";
  type: "mcq" | "written";
  instruction?: string;
  question?: string; // Specific question prompt
  questionText?: string; // Legacy
  specificQuestion?: string; // Legacy
  prompt?: string; // Legacy
  statement?: string; // Legacy
  text?: string;
  body?: string; // Legacy
  content?: string; // Legacy
  context?: string; // Legacy
  hasImage: boolean;
  imageUrl?: string;
  hasAudio: boolean;
  audioUrl?: string;
  alternatives?: {
    A: string;
    B: string;
    C: string;
    D: string;
  };
  options?: QuestionOption[];
  correctAnswer?: string;
  explanation?: string;
  tags?: string[];
  createdAt?: string;
  duplicateKey?: string;
  // Automatic difficulty fields (replaces manual selection)
  audioType?: string;        // e.g. "Micro-trottoirs" (Listening only)
  difficultyLabel?: string;  // e.g. "Médio a difícil"
  probableLevel?: string;    // e.g. "B1-B2"
  // Media Storage IDs (IndexedDB)
  audioMediaId?: string;
  imageMediaId?: string;
  subSection?: string;
  sub_section?: string;
  metadata?: Record<string, any>;
  media?: {
    image?: UploadedMedia;
    audio?: UploadedMedia;
    optionImages?: Record<string, UploadedMedia>;
  };
  importOrder?: number;
  questionNumber?: number;
  originalImportIndex?: number;
  temporaryImportId?: string;
}

const STORAGE_KEY = "tef_questions";

// Enriched mock questions in French for full-featured view
const initialQuestions: Question[] = [
  {
    id: "q1",
    title: "Anúncio Classificado",
    competence: "Reading",
    section: "Section A",
    difficulty: "Fácil",
    type: "mcq",
    instruction: "Lisez le document et répondez à la question.",
    text: "À LOUER : Bel appartement T2 au centre-ville de Lyon. Meublé, cuisine équipée, disponible immédiatement. Loyer: 750 € / mois, charges comprises. Chauffage individuel électrique. Contactez M. Dupont au 06.12.34.56.78 ou par e-mail : dupont.lyon@email.fr.",
    hasImage: true,
    imageUrl: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=800&q=80",
    hasAudio: false,
    alternatives: {
      A: "Cet appartement est situé à Paris et coûte 750 € hors charges.",
      B: "Le chauffage est collectif et compris dans le montant du loyer.",
      C: "La cuisine est équipée et l'appartement est prêt à être habité.",
      D: "L'appartement n'est pas meublé mais possède un garage privé."
    },
    correctAnswer: "C",
    explanation: "L'annonce indique explicitement 'Meublé, cuisine équipée, disponible immédiatement', ce qui confirme que la cuisine est équipée et qu'il est disponible tout de suite (prêt à être habité). La proposition B est fausse car le chauffage est individuel électrique.",
    tags: ["immobilier", "location", "annonces"],
    createdAt: "2026-05-01"
  },
  {
    id: "q2",
    title: "Mensagem Telefônica",
    competence: "Listening",
    section: "Section B",
    difficulty: "Médio",
    type: "mcq",
    instruction: "Écoutez le message audio et répondez à la question.",
    text: "[Enregistrement Audio] : Bonjour, c'est Sophie de l'agence VoyagePlus. Je t'appelle pour te confirmer que ton vol para Montréal est programmé pour demain à 14h30. N'oublie pas d'arriver à l'aéroport au moins 3 heures avant pour l'enregistrement de tes bagages. Bon voyage !",
    hasImage: false,
    hasAudio: true,
    audioUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3", // Public demo track
    alternatives: {
      A: "Le vol partira demain en fin de matinée.",
      B: "L'appelant demande d'arriver à l'aéroport 3 heures avant le départ.",
      C: "Sophie appelle pour annuler le voyage vers Montréal.",
      D: "Le voyageur doit acheter son billet de vol à l'aéroport."
    },
    correctAnswer: "B",
    explanation: "Sophie dit textuellement : 'N'oublie pas d'arriver à l'aéroport au moins 3 heures avant pour l'enregistrement'. Le départ à 14h30 correspond à l'après-midi, pas la matinée. Le vol est confirmé, pas annulé.",
    tags: ["voyage", "téléphone", "instructions"],
    createdAt: "2026-05-02"
  },
  {
    id: "q3",
    title: "Acidente na autoestrada",
    competence: "Writing",
    section: "Fait Divers",
    difficulty: "Difícil",
    type: "written",
    instruction: "Rédigez la suite de cet article de fait divers en respectant la structure journalistique standard (80 mots minimum).",
    text: "Un grave accident de la route s'est produit hier après-midi, vers 16h30, sur l'autoroute A7 en direction de Marseille. Une collision en chaîne impliquant trois voitures de tourisme et un poids lourd a complètement paralysé le trafic pendant plus de quatre heures. Selon les premiers éléments de l'enquête, une nappe de brouillard extrêmement dense et soudaine aurait surpris les conducteurs...",
    hasImage: false,
    hasAudio: false,
    correctAnswer: "",
    explanation: "Pour cette production écrite, veillez à utiliser les temps du passé (passé composé, imparfait, plus-que-parfait) pour relater les événements passés, mentionner les conséquences précises (blessés légers, dégâts matériels, embouteillage de 10 km) et raconter l'intervention efficace des pompiers et de la police de la route.",
    tags: ["accident", "autoroute", "faits divers"],
    createdAt: "2026-05-03"
  },
  {
    id: "q4",
    title: "Texto Argumentativo",
    competence: "Reading",
    section: "Section C",
    difficulty: "Médio",
    type: "mcq",
    instruction: "Lisez attentivement l'extrait d'article et choisissez la meilleure option.",
    text: "Le développement du télétravail au cours des dernières années a profondément modifié les équilibres sociaux. S'il apporte une autonomie indéniable et un gain de temps précieux lié à la réduction des trajets quotidiens, il soulève de vives inquiétudes chez les psychologues du travail. Ces derniers soulignent le sentiment d'isolement croissant exprimé par de nombreux employés et la difficulté croissante à tracer une frontière nette entre vie professionnelle et vie privée.",
    hasImage: false,
    hasAudio: false,
    alternatives: {
      A: "Le télétravail est unanimement considéré comme bénéfique pour la santé mentale.",
      B: "L'auteur présente un point de vue nuancé en évoquant à la fois des avantages et des risques.",
      C: "Le principal problème du télétravail est le coût élevé des équipements à domicile.",
      D: "Les psychologues recommandent l'interdiction définitive du travail à distance."
    },
    correctAnswer: "B",
    explanation: "L'auteur commence par mentionner des avantages du télétravail ('autonomie indéniable', 'gain de temps') puis bascule sur les inconvénients/risques via le connecteur oppositif 'S'il apporte... il soulève de vives inquiétudes' (isolement, rupture vie pro/perso), montrant un point de vue nuancé.",
    tags: ["sociologie", "travail", "télétravail"],
    createdAt: "2026-05-04"
  },
  {
    id: "q5",
    title: "Conversation 1",
    competence: "Listening",
    section: "Section A",
    difficulty: "Fácil",
    type: "mcq",
    instruction: "Écoutez l'annonce et choisissez l'image correspondante.",
    hasImage: true,
    hasAudio: true,
    audioUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
    options: [
      { label: "A", text: "", imageUrl: "https://images.unsplash.com/photo-1542640244-7e672d6cef21?auto=format&fit=crop&w=400&q=80" },
      { label: "B", text: "", imageUrl: "https://images.unsplash.com/photo-1474487548417-781cb71495f3?auto=format&fit=crop&w=400&q=80" },
      { label: "C", text: "", imageUrl: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=400&q=80" },
      { label: "D", text: "", imageUrl: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=400&q=80" }
    ],
    correctAnswer: "B",
    explanation: "L'annonce mentionne 'le train express', 'entrer en gare' et 'voie 3', ce qui correspond à la gare ferroviaire de l'image B. L'image A montre un métro, l'image C un aéroport et l'image D un autobus.",
    tags: ["transports", "voyage", "annonces"],
    createdAt: "2026-05-05"
  }
];

function generateUniqueId(index: number = 0): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch (e) {
      // Fallback
    }
  }
  return `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 11)}`;
}

export function ensureUniqueQuestionIds(newQuestions: any[], existingQuestions: Question[] = []): Question[] {
  const seenIds = new Set<string>();
  existingQuestions.forEach(q => {
    if (q.id) {
      seenIds.add(q.id);
    }
  });

  return newQuestions.map((q, index) => {
    let id = q.id;
    if (!id || seenIds.has(id)) {
      id = generateUniqueId(index);
    }
    seenIds.add(id);
    return {
      ...q,
      id
    } as Question;
  });
}

function normalizeStoredQuestions(): Question[] {
  const data = localStorage.getItem(STORAGE_KEY);
  if (!data) return [];
  
  try {
    const questions: Question[] = JSON.parse(data);
    const seen = new Set<string>();
    let changed = false;
    
    const normalized = questions.map((q, index) => {
      let id = q.id;
      if (!id || seen.has(id)) {
        id = generateUniqueId(index);
        changed = true;
      }
      seen.add(id);
      return { ...q, id };
    });

    if (changed) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    }
    return normalized;
  } catch (e) {
    console.error("Erro ao normalizar questões salvas:", e);
    return [];
  }
}

export function normalizeForDuplicateCheck(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .toLowerCase()
    .replace(/[^\w\s]/g, "") // remove punctuation
    .replace(/\s+/g, " ") // normalize whitespace
    .trim();
}

export async function generateDuplicateKey(question: any): Promise<string> {
  return await buildQuestionFingerprintHash(question);
}


// Translate React local Question representation to Supabase Schema Row
export function mapQuestionToSupabase(q: Partial<Question>): any {
  const options = q.options || (q.alternatives ? [
    { label: "A", text: q.alternatives.A },
    { label: "B", text: q.alternatives.B },
    { label: "C", text: q.alternatives.C },
    { label: "D", text: q.alternatives.D }
  ] : []);

  const mediaImage = q.media?.image || null;
  const mediaAudio = q.media?.audio || null;
  const mediaOptionImages = q.media?.optionImages || null;

  const resolvedSubSection = normalizeQuestionSubSection(
    q.subSection || q.sub_section || q.metadata?.subSection || q.metadata?.sub_section
  );

  const fingerprint = q.metadata?.importFingerprint || buildQuestionFingerprint(q);
  const rawFingerprintHash = q.metadata?.importFingerprintHash || q.duplicateKey || null;
  const fingerprintHash = rawFingerprintHash === "" ? null : rawFingerprintHash;

  const rawDuplicateKey = q.duplicateKey || fingerprintHash || null;
  const finalDuplicateKey = rawDuplicateKey === "" ? null : rawDuplicateKey;

  return {
    id: q.id,
    title: q.title || "",
    competence: q.competence || "Reading",
    section: q.section || "",
    difficulty: q.difficulty || "Médio",
    difficulty_level: q.difficultyLabel || q.probableLevel || "",
    tags: q.tags || [],
    instruction: q.instruction || "",
    context: q.text || "",
    options: options,
    correct_answer: q.correctAnswer || "",
    explanation: q.explanation || "",
    duplicate_key: finalDuplicateKey,
    media: {
      image: mediaImage,
      audio: mediaAudio,
      optionImages: mediaOptionImages,
      // Keep support for legacy properties
      hasImage: q.hasImage || !!mediaImage || false,
      imageUrl: q.imageUrl || mediaImage?.url || "",
      imageMediaId: q.imageMediaId || "",
      hasAudio: q.hasAudio || !!mediaAudio || false,
      audioUrl: q.audioUrl || mediaAudio?.url || "",
      audioMediaId: q.audioMediaId || ""
    },
    metadata: {
      ...(q.metadata || {}),
      importFingerprint: fingerprint,
      importFingerprintHash: fingerprintHash,
      type: q.type || q.metadata?.type || "mcq",
      sectionKey: q.sectionKey || q.metadata?.sectionKey || "",
      audioType: q.audioType || q.metadata?.audioType || "",
      difficultyLabel: q.difficultyLabel || q.metadata?.difficultyLabel || "",
      probableLevel: q.probableLevel || q.metadata?.probableLevel || "",
      alternatives: q.alternatives || q.metadata?.alternatives || null,
      subSection: resolvedSubSection,
      question: q.question || q.metadata?.question || "",
      questionText: q.questionText || q.metadata?.questionText || "",
      specificQuestion: q.specificQuestion || q.metadata?.specificQuestion || "",
      body: q.body || q.metadata?.body || "",
      importOrder: q.importOrder || q.metadata?.importOrder || null,
      questionNumber: q.questionNumber || q.metadata?.questionNumber || null,
      originalImportIndex: q.originalImportIndex !== undefined ? q.originalImportIndex : (q.metadata?.originalImportIndex !== undefined ? q.metadata.originalImportIndex : null),
      temporaryImportId: q.temporaryImportId || q.metadata?.temporaryImportId || null
    }
  };
}

// Translate Supabase Row Schema back to React local Question representation
export function mapSupabaseToQuestion(row: any): Question {
  const media = row.media || {};
  const metadata = row.metadata || {};
  
  const mediaImage: UploadedMedia | undefined = media.image || undefined;
  const mediaAudio: UploadedMedia | undefined = media.audio || undefined;
  const mediaOptionImages = media.optionImages || undefined;

  // Reconstruct alternatives if they exist in metadata, or from options
  let alternatives = metadata.alternatives || undefined;
  if (!alternatives && Array.isArray(row.options)) {
    const a = row.options.find((o: any) => o.label === "A")?.text;
    const b = row.options.find((o: any) => o.label === "B")?.text;
    const c = row.options.find((o: any) => o.label === "C")?.text;
    const d = row.options.find((o: any) => o.label === "D")?.text;
    if (a && b && c && d) {
      alternatives = { A: a, B: b, C: c, D: d };
    }
  }

  // Resolve alternative options with images
  let options = row.options;
  if (Array.isArray(options) && mediaOptionImages) {
    options = options.map((opt: any) => {
      const label = opt.label;
      const mediaOpt = mediaOptionImages[label];
      if (mediaOpt) {
        return {
          ...opt,
          imageUrl: mediaOpt.url
        };
      }
      return opt;
    });
  }

  const resolvedSubSection = normalizeQuestionSubSection(
    metadata.subSection || metadata.sub_section || row.subSection || row.sub_section
  );

  return {
    id: row.id,
    title: row.title,
    competence: normalizeQuestionCompetency(row.competence),
    section: row.section,
    sectionKey: metadata.sectionKey || undefined,
    subSection: resolvedSubSection,
    difficulty: row.difficulty,
    type: metadata.type || (row.correct_answer ? "mcq" : "written"),
    instruction: row.instruction,
    question: row.question || row.questionText || row.specificQuestion || metadata.question || metadata.prompt || "",
    text: row.context || row.text || row.body || row.content || "",
    body: row.body || row.context || "",
    questionText: row.questionText || "",
    specificQuestion: row.specificQuestion || "",
    hasImage: !!mediaImage || media.hasImage || false,
    imageUrl: mediaImage?.url || media.imageUrl || "",
    hasAudio: !!mediaAudio || media.hasAudio || false,
    audioUrl: mediaAudio?.url || media.audioUrl || "",
    alternatives,
    options: options || undefined,
    correctAnswer: row.correct_answer,
    explanation: row.explanation,
    tags: row.tags || [],
    createdAt: row.created_at ? new Date(row.created_at).toISOString().split("T")[0] : undefined,
    duplicateKey: row.duplicate_key || undefined,
    audioType: metadata.audioType || undefined,
    difficultyLabel: metadata.difficultyLabel || undefined,
    probableLevel: metadata.probableLevel || undefined,
    audioMediaId: media.audioMediaId || undefined,
    imageMediaId: media.imageMediaId || undefined,
    importOrder: metadata.importOrder !== null ? metadata.importOrder : undefined,
    questionNumber: metadata.questionNumber !== null ? metadata.questionNumber : undefined,
    originalImportIndex: metadata.originalImportIndex !== null ? metadata.originalImportIndex : undefined,
    temporaryImportId: metadata.temporaryImportId || undefined,
    metadata: {
      ...metadata,
      importFingerprintHash: metadata.importFingerprintHash,
      subSection: resolvedSubSection
    },
    media: {
      image: mediaImage,
      audio: mediaAudio,
      optionImages: mediaOptionImages
    }
  };
}

async function assertAdmin(): Promise<void> {
  if (isSupabaseConfigured && supabase) {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user?.email) {
      throw new Error("Você não tem permissão para modificar o banco de questões.");
    }
    const isAdmin = await adminService.isCurrentUserAdmin(userData.user);
    if (!isAdmin) {
      throw new Error("Você não tem permissão para modificar o banco de questões.");
    }
  }
}

export function normalizeCompetency(value: unknown): "reading" | "listening" | "writing" | "unknown" {
  const v = String(value || "").trim().toLowerCase();

  if (
    v === "listening" ||
    v === "co" ||
    v === "compréhension orale" ||
    v.includes("compréhension orale") ||
    v.includes("listening") ||
    v.includes("orale") ||
    v.includes("oral") ||
    /\bco\b/i.test(v)
  ) return "listening";

  if (
    v === "reading" ||
    v === "ce" ||
    v === "compréhension écrite" ||
    v.includes("compréhension écrite") ||
    v.includes("reading") ||
    /\bce\b/i.test(v)
  ) return "reading";

  if (
    v === "writing" ||
    v === "ee" ||
    v === "expression écrite" ||
    v.includes("expression écrite") ||
    v.includes("writing") ||
    /\bee\b/i.test(v)
  ) return "writing";

  return "unknown";
}

export const questionService = {
  // Backing in-memory variable to support synchronous counts & helpers
  _cachedQuestions: [] as Question[],
  isSupabaseConfigured,

  // Synchronous cache access
  getCachedQuestionsSync(): Question[] {
    if (this._cachedQuestions.length > 0) {
      return this._cachedQuestions;
    }
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(initialQuestions));
      this._cachedQuestions = initialQuestions;
      return initialQuestions;
    }
    const norm = normalizeStoredQuestions();
    this._cachedQuestions = norm;
    return norm;
  },

  getQuestionsSync(): Question[] {
    return this.getCachedQuestionsSync();
  },

  async getQuestionStats() {
    const questionsList = await this.getQuestions();
    let total = 0;
    let reading = 0;
    let listening = 0;
    let writing = 0;
    const bySection: Record<string, number> = {};

    questionsList.forEach(q => {
      total++;
      const comp = normalizeCompetency(q.competence ?? (q as any).competency ?? (q as any).skill ?? q.type);
      if (comp === "reading") reading++;
      else if (comp === "listening") listening++;
      else if (comp === "writing") writing++;

      const sec = q.section || "Geral";
      bySection[sec] = (bySection[sec] || 0) + 1;
    });

    return { total, reading, listening, writing, bySection };
  },

  // Get all questions with automatic duplicate/empty ID normalization
  async getQuestions(): Promise<Question[]> {
    if (isSupabaseConfigured && supabase) {
      try {
        const { data, error } = await supabase
          .from("questions")
          .select("*")
          .order("created_at", { ascending: false });
          
        if (error) throw error;
        
        const mapped = (data || []).map(mapSupabaseToQuestion);
        this._cachedQuestions = mapped;
        return mapped;
      } catch (e) {
        console.error("Erro ao buscar questões no Supabase, usando local storage:", e);
        return this.getCachedQuestionsSync();
      }
    } else {
      return this.getCachedQuestionsSync();
    }
  },

  // Get question by ID
  async getQuestionById(id: string): Promise<Question | undefined> {
    if (isSupabaseConfigured && supabase) {
      try {
        const { data, error } = await supabase
          .from("questions")
          .select("*")
          .eq("id", id)
          .single();
          
        if (error) throw error;
        if (!data) return undefined;
        return mapSupabaseToQuestion(data);
      } catch (e) {
        console.error("Erro ao buscar questão por ID no Supabase:", e);
        const local = this.getCachedQuestionsSync();
        return local.find(q => q.id === id);
      }
    } else {
      const local = this.getCachedQuestionsSync();
      return local.find(q => q.id === id);
    }
  },

  // Create new question
  async createQuestion(question: Omit<Question, "id"> & { id?: string }): Promise<Question> {
    streakService.registerActivity();
    await assertAdmin();

    // 1. Validation
    const validation = validateQuestion(question);
    if (!validation.isValid) {
      const err = new Error(`Erro de validação ao criar questão:\n${validation.errors.join("\n")}`);
      (err as any).code = "VALIDATION_ERROR";
      throw err;
    }
    
    // Check if the explicitly provided ID already exists to prevent duplicate IDs
    let finalId = question.id;
    if (!finalId) {
      finalId = generateUniqueId();
    }

    const newQuestion: Question = {
      ...question,
      id: finalId,
      createdAt: question.createdAt || new Date().toISOString().split("T")[0]
    };

    const generatedKey = await generateDuplicateKey(newQuestion);
    newQuestion.duplicateKey = generatedKey;
    newQuestion.metadata = {
      ...(newQuestion.metadata || {}),
      importFingerprintHash: generatedKey,
      importFingerprint: buildQuestionFingerprint(newQuestion),
      importFingerprintVersion: 2
    };

    let returnedQuestion = newQuestion;

    if (isSupabaseConfigured && supabase) {
      // 1. Direct search by duplicate_key
      const { data: exactDups, error: dupError } = await supabase
        .from("questions")
        .select("id")
        .eq("duplicate_key", generatedKey)
        .limit(1);

      if (exactDups && exactDups.length > 0) {
        const err = new Error("Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.");
        (err as any).code = "DUPLICATE_QUESTION";
        throw err;
      }

      // 2. Fallback check for older rows without duplicate_key set
      const { data: matchingRows, error: rowsError } = await supabase
        .from("questions")
        .select("id, title, competence, section, instruction, context")
        .eq("competence", newQuestion.competence)
        .eq("section", newQuestion.section);

      if (matchingRows && matchingRows.length > 0) {
        const duplicate = matchingRows.find(row => 
          normalizeForDuplicateCheck(row.title) === normalizeForDuplicateCheck(newQuestion.title) &&
          normalizeForDuplicateCheck(row.instruction) === normalizeForDuplicateCheck(newQuestion.instruction) &&
          normalizeForDuplicateCheck(row.context) === normalizeForDuplicateCheck(newQuestion.text || (newQuestion as any).context)
        );
        if (duplicate) {
          const err = new Error("Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.");
          (err as any).code = "DUPLICATE_QUESTION";
          throw err;
        }
      }

      const row = mapQuestionToSupabase(newQuestion);
      // Destructure to sanitize the payload. Prevent submitting invalid UUID temp id,
      // and allow database to use defaults for timestamps if not present.
      const { id, created_at, updated_at, ...insertPayload } = row;

      try {
        let res = await supabase
          .from("questions")
          .insert([insertPayload])
          .select()
          .single();

        if (res.error) throw res.error;

        if (res.data) {
          returnedQuestion = mapSupabaseToQuestion(res.data);
        }
      } catch (err: any) {
        console.error("[QUESTION_INSERT_FULL_ERROR]", JSON.stringify(err, null, 2));
        if (err.code === "PGRST204" || String(err.message || "").includes("PGRST204") || String(err.message || "").toLowerCase().includes("schema cache")) {
          console.error("[QUESTION_SCHEMA_FIELD_ERROR]", err);
          throw new Error("Não foi possível salvar a questão devido a um erro de esquema no banco (cache do Supabase desatualizado).");
        }
        if (err.code === "23505" || err.message?.includes("duplicate key")) {
          const customErr = new Error("Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.");
          (customErr as any).code = "DUPLICATE_QUESTION";
          throw customErr;
        }
        throw err;
      }
    } else {
      const questions = this.getCachedQuestionsSync();
      const duplicate = await (async () => {
        for (const q of questions) {
          const key = await generateDuplicateKey(q);
          if (key === generatedKey) return q;
        }
        return null;
      })();
      if (duplicate) {
        const err = new Error("Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.");
        (err as any).code = "DUPLICATE_QUESTION";
        throw err;
      }

      questions.push(newQuestion);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(questions));
    }

    // Refresh memory cache
    this._cachedQuestions = [];
    window.dispatchEvent(new Event("dashboardUpdated"));
    return returnedQuestion;
  },

  // Import multiple questions with unique ID validation
  async importQuestions(newQuestions: Omit<Question, "id"> & { id?: string }[]): Promise<{
    status: "success";
    insertedCount: number;
    skippedDuplicateCount: number;
    message: string;
    data: Question[];
  }> {
    streakService.registerActivity();
    await assertAdmin();
    
    // Get currently existing questions to pre-filter duplicates via fingerprinting
    const existingQuestions = await this.getQuestions();
    
    const existingFingerprints = new Set<string>();
    for (const eq of existingQuestions) {
      let fp = eq.metadata?.importFingerprintHash || eq.duplicateKey;
      if (!fp) {
        fp = await buildQuestionFingerprintHash(eq);
      }
      if (fp) {
        existingFingerprints.add(fp);
      }
    }
    
    const processed: any[] = [];
    for (const q of newQuestions) {
      const item = q as any;
      const metadata = item.metadata || {};
      
      const fingerprint = metadata.importFingerprint || buildQuestionFingerprint(item);
      const fingerprintHash = metadata.importFingerprintHash || (await buildQuestionFingerprintHash(item));
      const duplicateKey = item.duplicateKey || fingerprintHash;
      
      processed.push({
        ...item,
        createdAt: item.createdAt || new Date().toISOString().split("T")[0],
        duplicateKey,
        metadata: {
          ...metadata,
          importFingerprint: fingerprint,
          importFingerprintHash: fingerprintHash,
          importFingerprintVersion: 2
        }
      });
    }

    // Print the exact console logs specified:
    const newFingerprints = processed.map(q => q.duplicateKey);
    console.log("[CO_IMPORT_NEW_FINGERPRINTS]", newFingerprints);
    console.log("[CO_IMPORT_EXISTING_FINGERPRINTS]", Array.from(existingFingerprints));
    
    // Filter duplicates both from database and within the imported batch itself
    const uniqueToInsert: any[] = [];
    const duplicatesDetected: any[] = [];
    let skippedDuplicateCount = 0;
    const batchFingerprints = new Set<string>();

    for (const q of processed) {
      const fp = q.duplicateKey;
      if (existingFingerprints.has(fp) || batchFingerprints.has(fp)) {
        skippedDuplicateCount++;
        duplicatesDetected.push({
          title: q.title,
          duplicateKey: fp,
          reason: existingFingerprints.has(fp) ? "exists_in_database" : "duplicate_in_batch"
        });
      } else {
        batchFingerprints.add(fp);
        uniqueToInsert.push(q);
      }
    }

    console.log("[CO_IMPORT_DUPLICATES_BLOCKED]", duplicatesDetected);
    
    const uniqueNewQuestions = ensureUniqueQuestionIds(uniqueToInsert, existingQuestions);
    
    let insertedCount = 0;
    let returnedQuestions: Question[] = [];

    if (isSupabaseConfigured && supabase) {
      if (uniqueNewQuestions.length > 0) {
        const rows = uniqueNewQuestions.map(q => {
          const row = mapQuestionToSupabase(q as any);
          const cleanedId = row.id && row.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) ? row.id : null;
          if (!cleanedId) {
            delete row.id;
          }
          return row;
        });

        try {
          const { data, error } = await supabase
            .from("questions")
            .insert(rows)
            .select();

          if (error) {
            console.error("[QUESTION_INSERT_FULL_ERROR]", JSON.stringify(error, null, 2));
            console.error("[QUESTION_DUPLICATE_CONSTRAINT_RAW]", error);
            if (error.code === "PGRST204" || String(error.message || "").includes("PGRST204") || String(error.message || "").toLowerCase().includes("schema cache")) {
              console.error("[QUESTION_SCHEMA_FIELD_ERROR]", error);
              return {
                status: "success",
                insertedCount: 0,
                skippedDuplicateCount: uniqueNewQuestions.length,
                message: "Não foi possível sincronizar as novas questões com o banco devido a um erro de esquema (cache do Supabase desatualizado).",
                data: []
              };
            }
            if (error.code === "23505" || String(error.message || "").toLowerCase().includes("duplicate key")) {
              throw new Error("Esta questão já existe no banco e foi ignorada.");
            }
            throw error;
          } else {
            returnedQuestions = (data || []).map(mapSupabaseToQuestion);
            insertedCount = returnedQuestions.length;
          }
        } catch (err: any) {
          console.error("[QUESTION_INSERT_FULL_ERROR]", JSON.stringify(err, null, 2));
          console.error("[QUESTION_DUPLICATE_CONSTRAINT_RAW]", err);
          if (err.code === "PGRST204" || String(err.message || "").includes("PGRST204") || String(err.message || "").toLowerCase().includes("schema cache")) {
            console.error("[QUESTION_SCHEMA_FIELD_ERROR]", err);
            return {
              status: "success",
              insertedCount: 0,
              skippedDuplicateCount: uniqueNewQuestions.length,
              message: "Não foi possível sincronizar as novas questões com o banco devido a um erro de esquema (cache do Supabase desatualizado).",
              data: []
            };
          }
          if (err.code === "23505" || String(err.message || "").toLowerCase().includes("duplicate key")) {
            throw new Error("Esta questão já existe no banco e foi ignorada.");
          }
          throw err;
        }
      }
    } else {
      if (uniqueNewQuestions.length > 0) {
        const updatedList = [...existingQuestions, ...uniqueNewQuestions];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedList));
        
        this._cachedQuestions = updatedList;
        insertedCount = uniqueNewQuestions.length;
        returnedQuestions = uniqueNewQuestions;
      }
    }

    this._cachedQuestions = []; // Reset memory cache to force reload from DB
    window.dispatchEvent(new Event("dashboardUpdated"));
    
    return {
      status: "success",
      insertedCount,
      skippedDuplicateCount,
      message: `Importação concluída! Questões inseridas: ${insertedCount}. Duplicidades ignoradas: ${skippedDuplicateCount}.`,
      data: returnedQuestions
    };
  },

  // Update an existing question
  async updateQuestion(id: string, updatedQuestion: Partial<Question>): Promise<Question> {
    streakService.registerActivity();
    await assertAdmin();

    // 1. Merge and Validate
    const existing = await this.getQuestionById(id);
    if (!existing) {
      throw new Error(`Questão com ID ${id} não encontrada.`);
    }

    const mergedQuestion = {
      ...existing,
      ...updatedQuestion
    };

    const validation = validateQuestion(mergedQuestion);
    if (!validation.isValid) {
      const err = new Error(`Erro de validação ao atualizar questão:\n${validation.errors.join("\n")}`);
      (err as any).code = "VALIDATION_ERROR";
      throw err;
    }

    const generatedKey = await generateDuplicateKey(mergedQuestion as any);
    mergedQuestion.duplicateKey = generatedKey;
    mergedQuestion.metadata = {
      ...(mergedQuestion.metadata || {}),
      importFingerprintHash: generatedKey,
      importFingerprint: buildQuestionFingerprint(mergedQuestion),
      importFingerprintVersion: 2
    };
    
    if (isSupabaseConfigured && supabase) {
      // 1. Direct search by duplicate_key ignoring itself
      const { data: exactDups, error: dupError } = await supabase
        .from("questions")
        .select("id")
        .eq("duplicate_key", generatedKey)
        .neq("id", id)
        .limit(1);

      if (exactDups && exactDups.length > 0) {
        const err = new Error("Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.");
        (err as any).code = "DUPLICATE_QUESTION";
        throw err;
      }

      // 2. Fallback check for older rows without duplicate_key set ignoring itself
      const { data: matchingRows, error: rowsError } = await supabase
        .from("questions")
        .select("id, title, competence, section, instruction, context")
        .eq("competence", mergedQuestion.competence)
        .eq("section", mergedQuestion.section)
        .neq("id", id);

      if (matchingRows && matchingRows.length > 0) {
        const duplicate = matchingRows.find(row => 
          normalizeForDuplicateCheck(row.title) === normalizeForDuplicateCheck(mergedQuestion.title) &&
          normalizeForDuplicateCheck(row.instruction) === normalizeForDuplicateCheck(mergedQuestion.instruction) &&
          normalizeForDuplicateCheck(row.context) === normalizeForDuplicateCheck(mergedQuestion.text || (mergedQuestion as any).context)
        );
        if (duplicate) {
          const err = new Error("Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.");
          (err as any).code = "DUPLICATE_QUESTION";
          throw err;
        }
      }

      const row = mapQuestionToSupabase({ ...mergedQuestion, id } as any);
      // Strip identifiers from update payload to prevent changing primary key columns
      const { id: _, created_at, updated_at, ...updatePayload } = row;

      try {
        let res = await supabase.from("questions").update(updatePayload).eq("id", id);
        if (res.error) throw res.error;
      } catch (err: any) {
        if (err.code === "PGRST204" || String(err.message || "").includes("PGRST204") || String(err.message || "").toLowerCase().includes("schema cache")) {
          console.error("[QUESTION_SCHEMA_FIELD_ERROR]", err);
          throw new Error("Não foi possível salvar a questão devido a um erro de esquema no banco (cache do Supabase desatualizado).");
        }
        if (err.code === "23505" || err.message?.includes("duplicate key")) {
          const customErr = new Error("Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.");
          (customErr as any).code = "DUPLICATE_QUESTION";
          throw customErr;
        }
        throw err;
      }
      
      this._cachedQuestions = [];
      window.dispatchEvent(new Event("dashboardUpdated"));
      return { ...mergedQuestion, id } as Question;
    } else {
      const questions = this.getCachedQuestionsSync();
      const duplicate = await (async () => {
        for (const q of questions) {
          if (q.id === id) continue;
          const key = await generateDuplicateKey(q);
          if (key === generatedKey) return q;
        }
        return null;
      })();
      if (duplicate) {
        const err = new Error("Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.");
        (err as any).code = "DUPLICATE_QUESTION";
        throw err;
      }

      const index = questions.findIndex(q => q.id === id);
      if (index === -1) {
        throw new Error(`Questão com ID ${id} não encontrada.`);
      }

      const mergedQuestionLocal: Question = {
        ...questions[index],
        ...updatedQuestion,
        duplicateKey: generatedKey,
        id // Prevent ID modification
      };

      questions[index] = mergedQuestionLocal;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(questions));
      
      this._cachedQuestions = [];
      window.dispatchEvent(new Event("dashboardUpdated"));
      return mergedQuestionLocal;
    }
  },

  // Delete a question by ID
  async deleteQuestion(id: string): Promise<boolean> {
    await assertAdmin();
    if (isSupabaseConfigured && supabase) {
      const { error } = await supabase.from("questions").delete().eq("id", id);
      if (error) {
        console.error("Erro ao deletar questão no Supabase:", error);
        return false;
      }
      
      this._cachedQuestions = [];
      window.dispatchEvent(new Event("dashboardUpdated"));
      return true;
    } else {
      const questions = this.getCachedQuestionsSync();
      const question = questions.find(q => q.id === id);
      if (!question) {
        return false; // Not found
      }

      // Clean up media in IndexedDB
      const mediaIds = mediaService.collectMediaIds(question);
      if (mediaIds.length > 0) {
        await mediaService.deleteMediaBatch(mediaIds);
      }

      const filtered = questions.filter(q => q.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      
      this._cachedQuestions = [];
      window.dispatchEvent(new Event("dashboardUpdated"));
      return true;
    }
  },

  // Delete multiple questions by IDs
  async deleteQuestions(ids: string[]): Promise<boolean> {
    await assertAdmin();
    if (isSupabaseConfigured && supabase) {
      const { error } = await supabase.from("questions").delete().in("id", ids);
      if (error) {
        console.error("Erro ao deletar questões no Supabase:", error);
        return false;
      }
      
      this._cachedQuestions = [];
      window.dispatchEvent(new Event("dashboardUpdated"));
      return true;
    } else {
      const questions = this.getCachedQuestionsSync();
      const toDelete = questions.filter(q => ids.includes(q.id));
      if (toDelete.length === 0) {
        return false; // None of the questions were found/deleted
      }

      // Clean up media in IndexedDB
      const mediaIds: string[] = [];
      toDelete.forEach(q => {
        mediaIds.push(...mediaService.collectMediaIds(q));
      });
      if (mediaIds.length > 0) {
        await mediaService.deleteMediaBatch(mediaIds);
      }

      const filtered = questions.filter(q => !ids.includes(q.id));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      
      this._cachedQuestions = [];
      window.dispatchEvent(new Event("dashboardUpdated"));
      return true;
    }
  },

  // Safe migration routine from local to remote Supabase
  async migrateLocalQuestionsToSupabase(): Promise<{ successCount: number; duplicateCount: number; errorCount: number }> {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error("Supabase não está configurado.");
    }
    
    const localData = localStorage.getItem(STORAGE_KEY);
    if (!localData) return { successCount: 0, duplicateCount: 0, errorCount: 0 };
    
    try {
      const localQuestions: Question[] = JSON.parse(localData);
      
      // Fetch all existing supabase questions to check for duplicates
      const { data: supabaseRows, error } = await supabase
        .from("questions")
        .select("title, competence, section");
        
      if (error) throw error;
      
      const existingKeys = new Set(
        (supabaseRows || []).map((r: any) => `${r.title.trim().toLowerCase()}|${r.competence.trim().toLowerCase()}|${r.section.trim().toLowerCase()}`)
      );
      
      let successCount = 0;
      let duplicateCount = 0;
      let errorCount = 0;
      
      for (const q of localQuestions) {
        const key = `${q.title.trim().toLowerCase()}|${q.competence.trim().toLowerCase()}|${q.section.trim().toLowerCase()}`;
        if (existingKeys.has(key)) {
          duplicateCount++;
          continue;
        }
        
        const row = mapQuestionToSupabase(q);
        const { error: insertError } = await supabase
          .from("questions")
          .insert([row]);
          
        if (insertError) {
          console.error("Erro ao migrar questão:", insertError);
          errorCount++;
        } else {
          successCount++;
        }
      }
      
      return { successCount, duplicateCount, errorCount };
    } catch (e) {
      console.error("Erro na migração:", e);
      throw e;
    }
  },

  // Normalize section names to their official display label
  getNormalizedSection(q: { competence: string; section: string }): string {
    const comp = q.competence.toLowerCase();

    if (comp === "reading") {
      const key = normalizeReadingSection(q.section);
      return getReadingLabelByKey(key);
    } else if (comp === "listening") {
      const key = normalizeListeningSection(q.section);
      return getListeningLabelByKey(key);
    } else if (comp === "writing") {
      const key = normalizeWritingSection(q.section);
      return getWritingLabelByKey(key); // returns "Section A" or "Section B"
    }
    return q.section;
  },

  // Get active question counts grouped by official section structure
  getSectionCountsByCompetency(competency: string, customQuestions?: Question[]) {
    const questions = customQuestions || this.getCachedQuestionsSync();
    const compLower = competency.toLowerCase();

    if (compLower === "reading") {
      return READING_STRUCTURE.map(sec => {
        const count = questions.filter(q => {
          if (q.competence.toLowerCase() !== "reading") return false;
          return normalizeReadingSection(q.section) === sec.key;
        }).length;
        return {
          section: sec.label,
          key: sec.key,
          count,
          questionCount: sec.questionCount,
          description: sec.description
        };
      });
    }

    if (compLower === "listening") {
      return LISTENING_STRUCTURE.map(sec => {
        const count = questions.filter(q => {
          if (q.competence.toLowerCase() !== "listening") return false;
          return normalizeListeningSection(q.section) === sec.key;
        }).length;
        return {
          section: sec.label,
          key: sec.key,
          count,
          questionCount: sec.questionCount,
          description: sec.focus
        };
      });
    }

    // Writing — use WRITING_STRUCTURE for official section breakdown
    return WRITING_STRUCTURE.map(sec => {
      const count = questions.filter(q => {
        if (q.competence.toLowerCase() !== "writing") return false;
        return normalizeWritingSection(q.section) === sec.key;
      }).length;
      return {
        section: sec.label,
        key: sec.key,
        count,
        questionCount: sec.questionCount,
        description: `${sec.taskLabel} — mín. ${sec.minimumWords} palavras — ${sec.suggestedTimeMinutes} min sugeridos`
      };
    });
  },

  // Generate official reading test following exact chronological order and quantities
  generateReadingTest(allQuestions: Question[], selectedSection: string = "all", practiceMode: string = "practice", randomSeed: string): Question[] {
    const readingQuestions = allQuestions.filter(q => q.competence.toLowerCase() === "reading");
    
    if (practiceMode === "exam") {
      if (selectedSection === "all" || selectedSection === "") {
        return buildFullReadingTestQuestions(readingQuestions, randomSeed);
      } else {
        return buildSectionExamQuestions(readingQuestions, selectedSection, randomSeed);
      }
    }

    // Practice mode (treino livre): filter by section if needed
    if (selectedSection && selectedSection !== "all") {
      const normalized = normalizeReadingSection(selectedSection);
      return readingQuestions.filter(q => normalizeReadingSection(q.section) === normalized);
    }
    
    return readingQuestions;
  },

  // Generate balanced diagnostic test
  generateDiagnosticTest(allQuestions: Question[], competence: string, randomSeed: string): Question[] {
    const filtered = allQuestions.filter(q => q.competence.toLowerCase() === competence.toLowerCase());
    
    if (competence.toLowerCase() === "reading") {
      return buildDiagnosticQuestions(filtered, randomSeed);
    }

    // Fallback basic sampling for other competencies
    return selectRandomItems(filtered, 12);
  },

  // Generate official Listening test: Section A(8) → B(26) → C(4) → D(2)
  generateListeningTest(allQuestions: Question[], randomSeed: string): Question[] {
    const listeningQuestions = allQuestions.filter(q => q.competence.toLowerCase() === "listening");
    const remainingPool = [...listeningQuestions];

    const removeFromRemaining = (q: any) => {
      const idx = remainingPool.findIndex(r => r.id === q.id);
      if (idx !== -1) remainingPool.splice(idx, 1);
    };

    // Group by official section key
    const grouped: Record<string, Question[]> = {
      "Dialogues associés à des images": [],
      "Messages vocaux": [],
      "Annonces publiques": [],
      "Dialogues / Reportages courts": [],
      "Discussions / Conférences longues": []
    };

    listeningQuestions.forEach(q => {
      const key = normalizeListeningSection(q.section);
      if (grouped[key]) {
        grouped[key].push(q);
      } else {
        grouped["Messages vocaux"].push(q); // fallback to largest section
      }
    });

    const finalQuestions: Question[] = [];
    LISTENING_STRUCTURE.forEach(sec => {
      const pool = grouped[sec.key] || [];
      const selected = selectRandomItems(pool, sec.questionCount);
      if (selected.length < sec.questionCount) {
        console.warn("[ListeningFullTest] Questões insuficientes", {
          section: sec.key,
          required: sec.questionCount,
          available: selected.length,
        });
      }
      selected.forEach(q => {
        removeFromRemaining(q);
        finalQuestions.push({
          ...q,
          section: sec.label,  // enforce "Section A" / "B" / "C" / "D"
          sectionKey: sec.key
        });
      });
    });

    if (finalQuestions.length < 40 && remainingPool.length > 0) {
      const deficit = 40 - finalQuestions.length;
      const fallbackSelected = selectRandomItems(remainingPool, deficit);
      console.warn(`[ListeningFullTest] Preenchendo ${fallbackSelected.length} questões de outras seções para atingir 40.`);
      fallbackSelected.forEach(q => {
        finalQuestions.push({
          ...q,
          section: "Geral"
        });
      });
    }

    console.log("[ListeningFullTest] total selected", finalQuestions.length);
    return finalQuestions;
  },

  // Generate official Writing test: Section A (fait divers) → Section B (argumentation)
  generateWritingTest(allQuestions: Question[], randomSeed: string): Question[] {
    const writingQuestions = allQuestions.filter(q => q.competence.toLowerCase() === "writing");

    // Filtrar questões por seção usando os novos helpers para garantir precisão
    const sectionAPool = writingQuestions.filter(isWritingSectionA);
    const sectionBPool = writingQuestions.filter(isWritingSectionB);

    const configStr = localStorage.getItem("tef_trainer_settings");
    const config = configStr ? JSON.parse(configStr) : {};
    const shuffleQuestions = config.shuffle !== false;

    const finalQuestions: Question[] = [];

    // 1. Selecionar Section A
    const selectedA = selectRandomItems(sectionAPool, 1);
    if (selectedA.length > 0) {
      finalQuestions.push({
        ...selectedA[0],
        section: "Section A",
        sectionKey: "section_a"
      });
    } else {
      console.warn("[WritingFullTest] Nenhuma tarefa Section A disponível.");
    }

    // 2. Selecionar Section B
    const selectedB = selectRandomItems(sectionBPool, 1);
    if (selectedB.length > 0) {
      finalQuestions.push({
        ...selectedB[0],
        section: "Section B",
        sectionKey: "section_b"
      });
    } else {
      console.warn("[WritingFullTest] Nenhuma tarefa Section B disponível.");
    }

    // EE exige ordem fixa: Section A antes de Section B.
    // Não embaralhar o array final.
    console.log("[EE_V2_COMPLETE_TEST_SELECTION]", {
      sectionAPoolSize: sectionAPool.length,
      sectionBPoolSize: sectionBPool.length,
      selectedA: selectedA[0]?.id,
      selectedB: selectedB[0]?.id,
      selectedCount: finalQuestions.length,
    });

    console.log("[WritingFullTest] total selected", finalQuestions.length);
    return finalQuestions;
  },

  // Repair tool for legacy questions with embedded prompts
  async repairLegacyQuestions(): Promise<{ repairedCount: number; unrecoverableCount: number; errors: string[] }> {
    if (!this.isSupabaseConfigured || !supabase) {
      throw new Error("O Supabase não está configurado ou você não tem permissão.");
    }

    let repairedCount = 0;
    let unrecoverableCount = 0;
    const errors: string[] = [];

    try {
      const { data: allRows, error: fetchError } = await supabase
        .from("questions")
        .select("*");

      if (fetchError) throw fetchError;

      for (const row of allRows) {
        const q = mapSupabaseToQuestion(row);
        
        // Check if explicit question already exists in any known field
        const hasExplicitQuestion = !!(
          q.question?.trim() ||
          q.questionText?.trim() ||
          q.specificQuestion?.trim() ||
          q.prompt?.trim() ||
          q.statement?.trim() ||
          q.metadata?.question?.trim() ||
          q.metadata?.prompt?.trim()
        );

        if (hasExplicitQuestion) continue;

        // Try to extract from body
        const bodyText = q.text || q.body || q.content || q.context || "";
        const extraction = extractQuestionFromBody(bodyText);

        if (extraction.extractedQuestion) {
          // Found a question to extract!
          const updatedQuestion = {
            ...q,
            question: extraction.extractedQuestion,
            text: extraction.cleanBody,
            bodyText: extraction.cleanBody,
            body: extraction.cleanBody
          };

          const rowPayload = mapQuestionToSupabase(updatedQuestion);
          const { error: updateError } = await supabase
            .from("questions")
            .update(rowPayload)
            .eq("id", q.id);

          if (updateError) {
            errors.push(`Erro ao atualizar questão ${q.id}: ${updateError.message}`);
          } else {
            repairedCount++;
          }
        } else {
          unrecoverableCount++;
        }
      }
    } catch (err: any) {
      errors.push(`Erro fatal na reparação: ${err.message}`);
    }

    return { repairedCount, unrecoverableCount, errors };
  }
};


